import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  ClickAwayListener,
  IconButton,
  Menu,
  MenuItem,
  Popper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  RiArrowDownSLine,
  RiCalendarCheckLine,
  RiCalendarLine,
  RiCloseLine,
  RiDownload2Line,
  RiMore2Line,
  RiRestartLine,
  RiShoppingBag3Line,
  RiWallet3Line,
} from "@remixicon/react";
import {
  getDonationsPage,
  getLedgerAggregates,
  getLedgerPage,
  getPersonDonations,
  getPersonLedger,
  findStripePayment,
  readForExport,
} from "../../services/transactions";
import { searchPeople, getUsersByIds } from "../../services/dataService";
import { isMissingIndexError } from "../../services/firestoreErrors";
import { useAuth } from "../../contexts/AuthContext";
import {
  PERIODS,
  TABS,
  aggregateRows,
  cardTotals,
  kindOf,
  periodEnd,
  periodLabel,
  periodRange,
  tabCounts,
  visibleIn,
} from "../../utils/transactionLedger";
import {
  donationCsv,
  donationRowView,
  exportFileName,
  isStripeId,
  ledgerCsv,
  ledgerRowView,
  personOf,
  ymd,
} from "../../utils/transactionPresentation";
import { isSearchTerm } from "../../utils/studentPresentation";
import { sessionsLabel } from "../../utils/studentDetail";
import { money } from "../../utils/queuePresentation";
import { countText, showingOfLabel } from "../../utils/kitPresentation";
import { logUserActivity } from "../../utils/activityLogger";
import { focusRing, headButtonSx, srOnly, tokens } from "../../utils/figmaTokens";
import { ListHead } from "../../components/kit/PageHead";
import { TableCard } from "../../components/kit/SectionCard";
import { StateAction } from "../../components/kit/States";
import { SkeletonRows, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import Pager from "../../components/kit/Pager";
import SearchField from "../../components/kit/SearchField";
import SegmentTabs from "../../components/kit/SegmentTabs";
import StatusChip from "../../components/kit/StatusChip";
import ToolbarMenu from "../../components/kit/ToolbarMenu";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import { WHAT_LOOK } from "./whatLook";

// Money → Transactions (spec 044 D50–D59, plan slice 7 Task 4). Every wallet movement for a
// period, in tabs with counts, four cards and a CSV export. What this page reads: server
// counts and sums for the whole ledger (src/services/transactions.js getLedgerAggregates),
// one cursor page per tab (getLedgerPage / getDonationsPage), or — once a person is chosen —
// that person's own rows, read once and paged in memory. Nothing here writes except the
// export, which logs one EXPORT_TRANSACTIONS activity row.

const MISSING_INDEX = "This view needs a Firestore index that is missing or still building.";
const PAGE_SIZE = 25;

const LEDGER_COLUMNS = ["What", "Person", "Date", "Amount", "Type", "Actions"];
const DONATION_COLUMNS = ["Date", "Student", "Tutor", "Lesson", "Reason", "Source", "Fee (E£)", "Actions"];

const CardTile = ({ Icon, bg, fg, label, value, loading }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      p: "14px 18px",
      minWidth: 0,
      bgcolor: tokens.surface.default,
      border: `1px solid ${tokens.border.default}`,
      borderRadius: "14px",
    }}
  >
    <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", bgcolor: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon size={18} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 18, lineHeight: "30px", fontWeight: 500, color: tokens.text.primary }}>
        {loading || value == null ? "—" : value}
      </Typography>
      <Typography variant="labelMedium" noWrap sx={{ color: tokens.text.tertiary }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const CHECKOUT_OPTIONS = [
  { key: "hidden", label: "Hidden" },
  { key: "shown", label: "Shown" },
];

const Transactions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ["admin", "superAdmin"].includes(user?.role);
  const tabs = isAdmin ? TABS : TABS.filter((t) => t.key !== "donations");

  const [periodKey, setPeriodKey] = useState("thisMonth");
  const range = useMemo(() => periodRange(periodKey), [periodKey]);
  const [periodAnchor, setPeriodAnchor] = useState(null);

  const [tab, setTab] = useState("all");
  const [showCheckout, setShowCheckout] = useState(false);
  const [person, setPerson] = useState(null); // { id, name } | null
  const [stripe, setStripe] = useState(null); // { id, rows: null | array } | null — rows null = looking
  const [stripeError, setStripeError] = useState("");
  const stripeToken = useRef(0);

  // whole-ledger aggregates
  const [ledgerAggregates, setLedgerAggregates] = useState(null);
  // person-mode source rows
  const [personRows, setPersonRows] = useState(null);
  const [personDonations, setPersonDonations] = useState(null);
  const [personCapped, setPersonCapped] = useState(false);
  const [personError, setPersonError] = useState("");

  // whole-ledger table page
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState("");
  const cursors = useRef([]);
  const loadToken = useRef(0);
  const personToken = useRef(0);

  const [people, setPeople] = useState({});
  // Every write to `people` sets this first, so it is never a render behind.
  const peopleRef = useRef({});
  // Map<id, Promise>: one entry per id being (or already) read. A failed chunk deletes its
  // ids so a later call asks again, rather than marking them as known.
  const pendingNames = useRef(new Map());
  const nameChain = useRef(Promise.resolve());

  const [searchTerm, setSearchTerm] = useState("");
  const [peopleMatches, setPeopleMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const searchAnchorRef = useRef(null);

  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState("");
  const [rowMenu, setRowMenu] = useState(null);

  // R15: an id with no user document reads "Unknown account", so a row is never blocked on
  // a name. New ids are read in chunks of 50, one chunk after another (`nameChain`); every
  // id in flight — new or already started by an earlier call — is awaited before this
  // returns, so an export that starts while names are still loading waits for them. A
  // failed chunk logs and drops its ids from the map so a later call retries them, rather
  // than leaving them "known" and showing "Unknown account" for the rest of the visit.
  // Just before a chunk's read starts, every id in it that `people` does not already hold
  // gets a "…" placeholder (`getUsersByIds` never throws for a missing or a failed row, so
  // this is the only way a row would otherwise read "Unknown account" for one round trip);
  // once the chunk lands, the ids it found keep their real name and the rest lose the
  // placeholder, so `personOf` reads "Unknown account" for those, same as always.
  const ensureNames = useCallback(async (ledgerRows = [], donationRows = []) => {
    const ids = new Set();
    ledgerRows.forEach((r) => {
      if (r.userId) ids.add(r.userId);
      if (r.adjustedBy) ids.add(r.adjustedBy);
      if (r.transactionType === "referralReward") {
        const friend = String(r.reason || "").split(":")[2];
        if (friend) ids.add(friend);
      }
    });
    donationRows.forEach((d) => {
      if (d.studentId) ids.add(d.studentId);
      if (d.teacherId) ids.add(d.teacherId);
    });
    const all = [...ids];
    const fresh = all.filter((id) => !pendingNames.current.has(id));
    for (let i = 0; i < fresh.length; i += 50) {
      const chunk = fresh.slice(i, i + 50);
      nameChain.current = nameChain.current.then(async () => {
        const placeholderIds = chunk.filter((id) => !(id in peopleRef.current));
        if (placeholderIds.length > 0) {
          const withPlaceholders = { ...peopleRef.current };
          placeholderIds.forEach((id) => {
            withPlaceholders[id] = { name: "…" };
          });
          peopleRef.current = withPlaceholders;
          setPeople(withPlaceholders);
        }
        try {
          const found = await getUsersByIds(chunk);
          const merged = { ...peopleRef.current, ...found };
          placeholderIds.forEach((id) => {
            if (!found[id]) delete merged[id];
          });
          peopleRef.current = merged;
          setPeople(merged);
        } catch (e) {
          console.error("Transactions: name lookup failed:", e);
          const merged = { ...peopleRef.current };
          placeholderIds.forEach((id) => delete merged[id]);
          peopleRef.current = merged;
          setPeople(merged);
          chunk.forEach((id) => pendingNames.current.delete(id));
        }
      });
      chunk.forEach((id) => pendingNames.current.set(id, nameChain.current));
    }
    await Promise.all(all.map((id) => pendingNames.current.get(id)).filter(Boolean));
    return peopleRef.current;
  }, []);

  // Whole-ledger aggregates: reloaded only when the period or the person changes (R7/R12).
  useEffect(() => {
    if (person) return;
    let cancelled = false;
    setLedgerAggregates(null);
    getLedgerAggregates({ range, withDonations: isAdmin })
      .then((a) => {
        if (!cancelled) setLedgerAggregates(a);
      })
      .catch((e) => {
        console.error("Transactions: aggregates failed:", e);
        if (!cancelled) setLedgerAggregates({});
      });
    return () => {
      cancelled = true;
    };
  }, [range, person, isAdmin]);

  // Person mode (D55, R7): one bounded read, everything else derived from it in memory.
  const loadPerson = useCallback(
    async (uid) => {
      const token = ++personToken.current;
      setPersonRows(null);
      setPersonCapped(false);
      setPersonError("");
      try {
        // Donations are read apart: a failed donations read (admin only) leaves the ledger
        // read good — `personDonations` lands `null`, which the Donations tab reads as its
        // own failure rather than throwing away a good ledger read.
        const [ledgerData, donations] = await Promise.all([
          getPersonLedger({ uid, range }),
          isAdmin
            ? getPersonDonations({ uid, range }).catch((e) => {
                console.error("Transactions: person donations failed:", e);
                return null;
              })
            : Promise.resolve(null),
        ]);
        if (token !== personToken.current) return;
        setPersonRows(ledgerData.rows);
        setPersonCapped(ledgerData.capped);
        setPersonDonations(donations);
        ensureNames(ledgerData.rows, donations || []);
      } catch (e) {
        if (token !== personToken.current) return;
        console.error("Transactions: person ledger failed:", e);
        setPersonRows([]);
        setPersonDonations(isAdmin ? [] : null);
        setPersonError(isMissingIndexError(e) ? `${MISSING_INDEX} Nothing has been changed.` : "Nothing has been changed.");
      }
    },
    [range, isAdmin, ensureNames]
  );

  useEffect(() => {
    if (person) loadPerson(person.id);
  }, [person, loadPerson]);

  const personAggregates = useMemo(() => {
    if (!person || personRows === null || personError) return null;
    return { ...aggregateRows(personRows), donations: isAdmin ? (personDonations ? personDonations.length : null) : null };
  }, [person, personRows, personDonations, personError, isAdmin]);

  // On the Donations tab, `personDonations === null` once `personRows` has landed means the
  // donations read itself failed (the ledger read is good) — kept `null` here too, so the
  // table shows its own error row rather than "No donations in this period".
  const personTabRows = useMemo(() => {
    if (!person || personRows === null) return null;
    if (tab === "donations") return personDonations;
    return personRows.filter((r) => visibleIn(tab, kindOf(r), { showCheckout }));
  }, [person, personRows, personDonations, tab, showCheckout]);

  const aggregates = person ? personAggregates : ledgerAggregates;
  const aggregatesLoading = person ? personRows === null && !personError : ledgerAggregates === null;

  // Whole-ledger table page (Students' cursor pattern).
  const loadTablePage = useCallback(
    async (nextPage) => {
      const token = ++loadToken.current;
      setTableLoading(true);
      try {
        const cursor = nextPage === 0 ? null : cursors.current[nextPage - 1] || null;
        const data =
          tab === "donations"
            ? await getDonationsPage({ range, start: cursor, pageSize: PAGE_SIZE })
            : await getLedgerPage({ tab, range, showCheckout, start: cursor, pageSize: PAGE_SIZE });
        if (token !== loadToken.current) return;
        cursors.current[nextPage] = data.next;
        setRows(data.rows);
        setHasMore(data.hasMore);
        setPage(nextPage);
        setTableError("");
        if (tab === "donations") ensureNames([], data.rows);
        else ensureNames(data.rows, []);
      } catch (e) {
        if (token !== loadToken.current) return;
        console.error("Transactions: table load failed:", e);
        setTableError(isMissingIndexError(e) ? `${MISSING_INDEX} Nothing has been changed.` : "Nothing has been changed.");
      } finally {
        if (token === loadToken.current) setTableLoading(false);
      }
    },
    [tab, range, showCheckout, ensureNames]
  );

  // A new tab, period, switch or person starts again at page 1 with fresh cursors.
  useEffect(() => {
    cursors.current = [];
    setPage(0);
    setHasMore(false);
    if (!person && !stripe) loadTablePage(0);
  }, [tab, range, showCheckout, person, stripe, loadTablePage]);

  // Search: a Stripe id (isStripeId) waits for Enter; anything else that reaches search
  // length debounces into a people search (R7/R8).
  useEffect(() => {
    if (isStripeId(searchTerm) || !isSearchTerm(searchTerm)) {
      setPeopleMatches(null);
      setSearching(false);
      return undefined;
    }
    let stale = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const found = await searchPeople(searchTerm, { role: "student" });
        if (stale) return;
        setPeopleMatches(found.slice(0, 5));
      } catch (e) {
        console.error("Transactions: person search failed:", e);
        if (!stale) setPeopleMatches([]);
      } finally {
        if (!stale) setSearching(false);
      }
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // The lookup is visible from the first moment (`rows: null` = looking), and can land
  // late: `stripeToken` is bumped here and by anything else that clears `stripe`
  // (clearStripe, chooseTab, pickPerson), so a stale result never overwrites a newer one.
  // The period does not bump it — a lookup does not depend on the period.
  const runStripeLookup = async (term) => {
    const id = term.trim();
    const token = ++stripeToken.current;
    setPerson(null);
    setStripe({ id, rows: null });
    setStripeError("");
    try {
      const found = await findStripePayment(id);
      if (token !== stripeToken.current) return;
      setStripe({ id, rows: found });
      ensureNames(found, []);
    } catch (e) {
      if (token !== stripeToken.current) return;
      console.error("Transactions: Stripe lookup failed:", e);
      setStripe({ id, rows: [] });
      setStripeError(isMissingIndexError(e) ? `${MISSING_INDEX} Nothing has been changed.` : "Nothing has been changed.");
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (isStripeId(term)) runStripeLookup(term);
  };

  const pickPerson = (p) => {
    stripeToken.current += 1;
    setStripe(null);
    setStripeError("");
    const view = personOf(p.id, { [p.id]: p });
    setPerson({ id: p.id, name: view.name });
    setSearchTerm("");
    setPeopleMatches(null);
    setListOpen(false);
  };

  const clearStripe = () => {
    stripeToken.current += 1;
    setStripe(null);
    setStripeError("");
    setSearchTerm("");
  };

  const showEveryone = () => setPerson(null);

  const chooseTab = (key) => {
    stripeToken.current += 1;
    setStripe(null);
    setStripeError("");
    setTab(key);
  };

  // Table rows to render, by mode.
  const stripeMode = Boolean(stripe);
  const personMode = Boolean(person) && !stripeMode;
  const isDonationsTab = !stripeMode && tab === "donations";
  let displayRows;
  let rowsLoading;
  let rowsErrorMessage;
  let rowsHasMore = false;
  let showPager = true;
  if (stripeMode) {
    displayRows = stripe.rows || [];
    rowsLoading = stripe.rows === null;
    rowsErrorMessage = stripeError;
    showPager = false;
  } else if (personMode) {
    // A donations read that failed while the ledger read itself is good: its own error,
    // never mistaken for "still loading" (personTabRows stays null) or "no donations".
    const donationsFailed = isDonationsTab && personRows !== null && !personError && personDonations === null;
    displayRows = personTabRows === null ? [] : personTabRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    rowsLoading = personTabRows === null && !personError && !donationsFailed;
    rowsErrorMessage = personError || (donationsFailed ? "Nothing has been changed." : "");
    rowsHasMore = personTabRows ? (page + 1) * PAGE_SIZE < personTabRows.length : false;
  } else {
    displayRows = rows;
    rowsLoading = tableLoading;
    rowsErrorMessage = tableError;
    rowsHasMore = hasMore;
  }

  const columns = isDonationsTab ? DONATION_COLUMNS : LEDGER_COLUMNS;
  // The "Find the Stripe payment" option, until that id's lookup is the one on screen.
  const stripeOption = isStripeId(searchTerm) && stripe?.id !== searchTerm.trim();
  // JSX children are built even while the Popper is closed, so the list is built only when
  // shown: then `peopleMatches` is set whenever neither of the other two is.
  const listShown = listOpen && Boolean(stripeOption || peopleMatches || searching);
  const failed = Boolean(rowsErrorMessage) && !rowsLoading;
  const empty = !rowsLoading && !failed && displayRows.length === 0;

  const emptyCopy = () => {
    if (stripeMode) return { title: "No payment with this Stripe id", message: "Check the id and try again." };
    if (isDonationsTab) return { title: "No donations in this period", message: "Try another period or tab." };
    if (person) return { title: `No transactions for ${person.name} in this period`, message: "Try another period or tab." };
    return { title: "No transactions in this period", message: "Try another period or tab." };
  };

  const retry = () => {
    if (stripeMode) runStripeLookup(stripe.id);
    else if (personMode) loadPerson(person.id);
    else loadTablePage(page);
  };

  const tabCountsValue = aggregatesLoading ? {} : tabCounts(aggregates || {}, { showCheckout });
  const totals = cardTotals(aggregates || {});

  const copyStripeId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setToast("Stripe id copied");
    } catch {
      setToast("Could not copy the Stripe id");
    }
  };

  // R14: the items a row's ⋮ opens — only what applies. A row with none renders no button.
  const ledgerMenuItems = (view) => {
    const items = [];
    if (view.personId) items.push({ key: "person", label: "Open person", onClick: () => navigate(`/students/${view.personId}`) });
    if (view.lessonId) items.push({ key: "lesson", label: "Open lesson", onClick: () => navigate(`/lessons/${view.lessonId}`) });
    if (view.stripeId) items.push({ key: "stripe", label: "Copy Stripe id", onClick: () => copyStripeId(view.stripeId) });
    return items;
  };
  const donationMenuItems = (d) => {
    const items = [];
    if (d.studentId) items.push({ key: "student", label: "Open student", onClick: () => navigate(`/students/${d.studentId}`) });
    if (d.teacherId) items.push({ key: "tutor", label: "Open tutor", onClick: () => navigate(`/tutors/${d.teacherId}`) });
    if (d.lessonId) items.push({ key: "lesson", label: "Open lesson", onClick: () => navigate(`/lessons/${d.lessonId}`) });
    return items;
  };
  const openRowMenu = (e, id, items) => setRowMenu({ anchorEl: e.currentTarget, id, items });
  const closeRowMenu = () => setRowMenu(null);

  const doExport = async () => {
    setExporting(true);
    try {
      let exportRows;
      let capped = false;
      let personCappedExport = false;
      if (person) {
        exportRows = isDonationsTab ? personDonations || [] : personTabRows || [];
        personCappedExport = personCapped;
      } else {
        const result = await readForExport({ tab, range, showCheckout });
        exportRows = result.rows;
        capped = result.capped;
      }
      const namesFor = isDonationsTab
        ? await ensureNames([], exportRows)
        : await ensureNames(exportRows, []);
      const csv = isDonationsTab ? donationCsv(exportRows, namesFor) : ledgerCsv(exportRows, namesFor);
      const fileName = exportFileName(tab, range, new Date());
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);

      const from = ymd(range.from);
      const to = ymd(periodEnd(range, new Date()));
      const tabLabel = tabs.find((t) => t.key === tab)?.label || tab;
      await logUserActivity({
        userId: user?.uid || user?.id || "unknown",
        userName: user?.name || user?.displayName || "Unknown User",
        userEmail: user?.email || "unknown@example.com",
        action: "EXPORT_TRANSACTIONS",
        entityType: "transactions",
        entityId: tab,
        entityName: fileName,
        details: { tab, from, to, rows: exportRows.length, person: person?.id || null },
        description: `${user?.name || "User"} exported ${exportRows.length} transactions (${tabLabel})`,
      });

      if (person) {
        setToast(
          personCappedExport
            ? `Exported ${countText(exportRows.length)} rows from the newest 1,000 for ${person.name}.`
            : `Exported ${countText(exportRows.length)} rows.`
        );
      } else if (capped) {
        setToast("Exported the first 10,000 rows. Pick a shorter period for the rest.");
      } else {
        setToast(`Exported ${countText(exportRows.length)} rows.`);
      }
    } catch (e) {
      console.error("Transactions: export failed:", e);
      setToast("Could not export. Nothing has been changed.");
    } finally {
      setExporting(false);
    }
  };

  const CARDS = [
    { key: "collected", label: "Collected", value: totals.collected == null ? null : `£${money(totals.collected)}`, Icon: RiWallet3Line, bg: tokens.brand.subtle, fg: tokens.icon.brand },
    { key: "sold", label: "Sessions sold", value: totals.sold == null ? null : sessionsLabel(totals.sold), Icon: RiShoppingBag3Line, bg: tokens.success.bg, fg: tokens.success.main },
    { key: "used", label: "Sessions used", value: totals.used == null ? null : sessionsLabel(totals.used), Icon: RiCalendarCheckLine, bg: tokens.info.bg, fg: tokens.info.main },
    { key: "refunded", label: "Sessions refunded", value: totals.refunded == null ? null : sessionsLabel(totals.refunded), Icon: RiRestartLine, bg: tokens.warning.bg, fg: tokens.warning.main },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <ListHead caption="Money" title="Transactions" subtitle="Every payment and session that moved through the wallets" />
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Button
            id="transactions-period-button"
            onClick={(e) => setPeriodAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(periodAnchor)}
            aria-controls={periodAnchor ? "transactions-period-menu" : undefined}
            disableRipple
            disableFocusRipple
            sx={headButtonSx}
          >
            <RiCalendarLine size={18} />
            {periodLabel(range)}
            <RiArrowDownSLine size={16} />
          </Button>
          <Menu
            id="transactions-period-menu"
            anchorEl={periodAnchor}
            open={Boolean(periodAnchor)}
            onClose={() => setPeriodAnchor(null)}
            slotProps={{ list: { "aria-labelledby": "transactions-period-button" } }}
          >
            {PERIODS.map((p) => (
              <MenuItem
                key={p.key}
                selected={p.key === periodKey}
                onClick={() => {
                  setPeriodAnchor(null);
                  setPeriodKey(p.key);
                }}
              >
                {p.label}
              </MenuItem>
            ))}
          </Menu>
          <Button
            onClick={doExport}
            disabled={exporting || stripeMode || (personMode && (personRows === null || (isDonationsTab && personDonations === null)))}
            disableRipple
            disableFocusRipple
            sx={headButtonSx}
          >
            <RiDownload2Line size={18} />
            {exporting ? "Exporting…" : "Export"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" } }}>
        {CARDS.map((c) => (
          <CardTile key={c.key} Icon={c.Icon} bg={c.bg} fg={c.fg} label={c.label} value={c.value} loading={aggregatesLoading} />
        ))}
      </Box>

      <TableCard
        toolbar={
          <>
            <SegmentTabs label="Transaction type" tabs={tabs} value={tab} counts={tabCountsValue} onChange={chooseTab} />
            <Box sx={{ flex: 1, minWidth: 12 }} />
            <ClickAwayListener onClickAway={() => setListOpen(false)}>
              <Box
                component="form"
                role="search"
                ref={searchAnchorRef}
                onSubmit={handleSearchSubmit}
                onFocus={() => setListOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setSearchTerm("");
                    setListOpen(false);
                  }
                }}
                sx={{ position: "relative" }}
              >
                <SearchField
                  value={searchTerm}
                  onChange={(v) => {
                    setSearchTerm(v);
                    setListOpen(true);
                  }}
                  placeholder="Search a person or a Stripe id"
                />
                {/* Popper portals the list out of TableCard's overflow: hidden, so it is never clipped. */}
                <Popper
                  open={listShown}
                  anchorEl={searchAnchorRef.current}
                  placement="bottom-start"
                  sx={{ zIndex: 1300, width: searchAnchorRef.current?.offsetWidth }}
                >
                  {listShown && (
                    <Box
                      role="listbox"
                      aria-label="People found"
                      sx={{
                        mt: "6px",
                        maxHeight: 280,
                        overflowY: "auto",
                        bgcolor: tokens.surface.default,
                        border: `1px solid ${tokens.border.default}`,
                        borderRadius: "12px",
                        boxShadow: tokens.shadow.menu,
                        p: "6px",
                      }}
                    >
                      {stripeOption ? (
                        <ButtonBase
                          role="option"
                          aria-selected={false}
                          onClick={() => {
                            setListOpen(false);
                            runStripeLookup(searchTerm);
                          }}
                          disableRipple
                          sx={{ width: "100%", flexDirection: "column", alignItems: "flex-start", gap: "2px", p: "8px 10px", borderRadius: "8px", textAlign: "left", "&:hover": { bgcolor: tokens.hover }, ...focusRing }}
                        >
                          <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
                            Find the Stripe payment
                          </Typography>
                          <Typography variant="labelMedium" noWrap sx={{ color: tokens.text.secondary }}>
                            {searchTerm.trim()}
                          </Typography>
                          <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
                            or press Enter
                          </Typography>
                        </ButtonBase>
                      ) : searching ? (
                        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary, p: "10px 12px" }}>
                          Searching…
                        </Typography>
                      ) : peopleMatches.length === 0 ? (
                        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary, p: "10px 12px" }}>
                          No student matches “{searchTerm.trim()}”.
                        </Typography>
                      ) : (
                        peopleMatches.map((p) => (
                          <ButtonBase
                            key={p.id}
                            role="option"
                            aria-selected={false}
                            onClick={() => pickPerson(p)}
                            disableRipple
                            sx={{
                              width: "100%",
                              justifyContent: "flex-start",
                              gap: "10px",
                              p: "8px 10px",
                              borderRadius: "8px",
                              textAlign: "left",
                              "&:hover": { bgcolor: tokens.hover },
                              ...focusRing,
                            }}
                          >
                            <InitialsAvatar name={p.name || p.email || "?"} colorKey={p.id} size={32} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                                {p.name || "Unknown"}
                              </Typography>
                              <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
                                {p.email || "No contact"}
                              </Typography>
                            </Box>
                          </ButtonBase>
                        ))
                      )}
                    </Box>
                  )}
                </Popper>
              </Box>
            </ClickAwayListener>
            {person && (
              <Chip
                label={person.name}
                onClick={showEveryone}
                onDelete={showEveryone}
                aria-label={`Show everyone (clear ${person.name})`}
                deleteIcon={<RiCloseLine size={15} aria-hidden />}
              />
            )}
            <ToolbarMenu
              id="transactions-checkout"
              label="Checkout started"
              value={showCheckout ? "shown" : "hidden"}
              options={CHECKOUT_OPTIONS}
              onChange={(k) => setShowCheckout(k === "shown")}
              disabled={tab !== "all" && tab !== "topups"}
              hint="Checkout rows belong to All and Top-ups"
            />
          </>
        }
        footer={
          showPager && (
            <Pager
              label={rowsLoading || failed ? "" : showingOfLabel(page, displayRows.length, PAGE_SIZE, tabCountsValue[tab])}
              page={page}
              hasMore={rowsHasMore}
              onPage={personMode ? setPage : loadTablePage}
            />
          )
        }
      >
        {stripeMode && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", px: "24px", py: "10px" }}>
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
              Stripe id {stripe.id} ·{" "}
              {stripe.rows === null ? "looking…" : `${countText(stripe.rows.length)} ${stripe.rows.length === 1 ? "row" : "rows"}`}
            </Typography>
            <StateAction outlined onClick={clearStripe}>
              Clear
            </StateAction>
          </Box>
        )}
        {personMode && personCapped && (
          <Box sx={{ px: "24px", py: "8px" }}>
            <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
              Showing the newest 1,000 rows for {person.name} in this period — the counts and cards cover these rows only.
            </Typography>
          </Box>
        )}
        <TableContainer>
          <Table aria-busy={rowsLoading}>
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c} align={c === "Amount" || c.startsWith("Fee") ? "right" : "left"}>
                    {c === "Actions" ? <Box component="span" sx={srOnly}>{c}</Box> : c}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsLoading ? (
                <SkeletonRows columns={columns.length} />
              ) : failed || empty ? (
                <TableMessageRow
                  colSpan={columns.length}
                  error={failed ? "Could not load transactions" : ""}
                  errorMessage={rowsErrorMessage}
                  onRetry={retry}
                  emptyText={emptyCopy().title}
                  emptyMessage={emptyCopy().message}
                />
              ) : isDonationsTab ? (
                displayRows.map((d) => {
                  const view = donationRowView(d, people);
                  const items = donationMenuItems(view);
                  return (
                    <TableRow key={view.id}>
                      <TableCell sx={{ whiteSpace: "nowrap", color: tokens.text.secondary }}>{view.date}</TableCell>
                      <TableCell>
                        <TwoLineCell top={view.student.name} bottom={view.student.sub} />
                      </TableCell>
                      <TableCell>
                        <TwoLineCell top={view.tutor.name} bottom={view.tutor.sub} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200 }}>
                          <Typography variant="bodySmall" noWrap title={view.lessonId || undefined} sx={{ color: tokens.text.primary }}>
                            {view.lessonId || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 240, overflowWrap: "anywhere" }}>{view.reason}</Box>
                      </TableCell>
                      <TableCell>{view.source}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                        {view.fee}
                      </TableCell>
                      <TableCell>
                        {items.length > 0 && (
                          <IconButton
                            aria-label={`Actions for ${view.student.name}`}
                            aria-haspopup="menu"
                            aria-expanded={rowMenu?.id === view.id}
                            aria-controls={rowMenu?.id === view.id ? "transactions-row-menu" : undefined}
                            onClick={(e) => openRowMenu(e, view.id, items)}
                            disableFocusRipple
                            sx={{ width: 36, height: 32, borderRadius: "8px", color: tokens.icon.default, "&:hover": { bgcolor: tokens.hover }, ...focusRing }}
                          >
                            <RiMore2Line size={18} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                displayRows.map((t) => {
                  const view = ledgerRowView(t, people);
                  const look = WHAT_LOOK[view.look] || WHAT_LOOK.other;
                  const items = ledgerMenuItems(view);
                  return (
                    <TableRow key={view.id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
                          <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", bgcolor: look.tone.bg, color: look.tone.main, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <look.Icon size={18} />
                          </Box>
                          <Box sx={{ minWidth: 0, maxWidth: 320 }}>
                            <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                              {view.title}
                            </Typography>
                            {view.sub && (
                              <Typography variant="labelMedium" component="div" noWrap title={view.sub} sx={{ color: tokens.text.tertiary }}>
                                {view.sub}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <TwoLineCell top={view.person.name} bottom={view.person.sub} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: tokens.text.secondary }}>{view.date}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: "nowrap",
                          fontSize: 14,
                          lineHeight: "22px",
                          fontWeight: 500,
                          color: view.negative ? tokens.error.ink : view.muted ? tokens.text.tertiary : tokens.text.primary,
                        }}
                      >
                        {view.amount}
                      </TableCell>
                      <TableCell>
                        <StatusChip label={view.pill} color={view.tone} />
                      </TableCell>
                      <TableCell>
                        {items.length > 0 && (
                          <IconButton
                            aria-label={`Actions for ${view.title}`}
                            aria-haspopup="menu"
                            aria-expanded={rowMenu?.id === view.id}
                            aria-controls={rowMenu?.id === view.id ? "transactions-row-menu" : undefined}
                            onClick={(e) => openRowMenu(e, view.id, items)}
                            disableFocusRipple
                            sx={{ width: 36, height: 32, borderRadius: "8px", color: tokens.icon.default, "&:hover": { bgcolor: tokens.hover }, ...focusRing }}
                          >
                            <RiMore2Line size={18} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <Menu id="transactions-row-menu" anchorEl={rowMenu?.anchorEl} open={Boolean(rowMenu)} onClose={closeRowMenu}>
        {(rowMenu?.items || []).map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => {
              closeRowMenu();
              item.onClick();
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast("")} message={toast} />
    </Box>
  );
};

export default Transactions;
